import { useState, useEffect } from 'react';
import { X, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'ios-install-banner-dismissed';

export const IOSInstallBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Only show install prompt to authenticated users — never on public/marketing pages.
    if (!user) {
      setIsVisible(false);
      return;
    }
    // Check if running on iOS Safari and not already installed as PWA
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               (window.navigator as any).standalone === true;
    const isDismissed = localStorage.getItem(STORAGE_KEY) === 'true';
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome|CriOS|FxiOS/.test(navigator.userAgent);

    if (isIOS && isSafari && !isInStandaloneMode && !isDismissed) {
      // Delay showing to avoid overwhelming users immediately
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] md:bottom-0 left-0 right-0 z-50 p-4 bg-background/95 backdrop-blur-sm border-t border-border shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="max-w-md mx-auto">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">
              Install LupusLogix
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Add to your home screen for the best experience:
            </p>
            <ol className="text-sm text-muted-foreground space-y-2">
              <li className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium">1</span>
                <span>Tap the <Share className="inline w-4 h-4 text-primary" /> <strong>Share</strong> button</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium">2</span>
                <span>Tap <Plus className="inline w-4 h-4 text-primary" /> <strong>"Add to Home Screen"</strong></span>
              </li>
            </ol>
            <p className="text-xs text-muted-foreground/70 italic mt-2">
              Note: Must use Safari. Other browsers don't support this on iOS.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="shrink-0 h-8 w-8"
            aria-label="Dismiss install banner"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDismiss}
          className="w-full mt-3"
        >
          Maybe Later
        </Button>
      </div>
    </div>
  );
};

export default IOSInstallBanner;
